import React from "react";
import PropTypes from "prop-types";
import { Dispatcher, BookUtils } from "../../../flux";
import { Isvg, ZoomButton } from "../../../components";
import { Select } from "@vitalsource/vstui";
import styled from "styled-components";
import BookHighlights from "./book-highlights";
import BookSearch from "./book-search";

const InlineSelect = styled(Select)`
  display: flex;
  align-items: center;
  margin: 0;
  label {
    margin: 0;
    min-width: 35px;
  }
`;

const GoToPageInputContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  input {
    min-width: 74px;
  }
  label {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 500;
    min-width: 35px;
  }
`;

export default class BookReader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      pageNumber: 0,
      defaultPageNumber: 0,
      pagelist: null, // Page breaks for snippet
      pageBreaks: [], // Page breaks for book
      displaySearch: false,
      displayHighlights: false,
      displayZoom: false
    };

    this._getCfiFromPageNumber = this._getCfiFromPageNumber.bind(this);
    this._toggleSearch = this._toggleSearch.bind(this);
  }

  UNSAFE_componentWillUpdate() {
    if (this.props.reloadPageList) {
      this.loadPageList();
    }
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.BOOK_SNIPPET_READY, "abook-snippet-break");
    Dispatcher.unbind(
      Dispatcher.BOOK_PAGEBREAKS_LOADED,
      "abook-pagebreak-loaded"
    );
    Dispatcher.unbind(Dispatcher.BOOK_PAGEBREAK, "assignment-book-page-break");
    Dispatcher.unbind(Dispatcher.BOOK_CHANGE, "abook-change");
    if (!this.state.firedPageReady) {
      Dispatcher.unbind(
        Dispatcher.BOOK_PAGE_READY,
        "assignment-page-ready-once-event"
      );
    }
  }

  componentDidMount() {
    this.props.book.getPageBreaks &&
      this.setState({ pageBreaks: this.props.book.getPageBreaks() });
    Dispatcher.bindOnce(
      Dispatcher.BOOK_PAGE_READY,
      () => {
        this.setState({
          pageBreaks: this.props.book.getPageBreaks(),
          firedPageReady: true
        });
      },
      "assignment-page-ready-once-event"
    );

    Dispatcher.bind(
      Dispatcher.BOOK_PAGEBREAKS_LOADED,
      this.loadPageList.bind(this),
      "abook-snippet-break"
    );
    Dispatcher.bind(
      Dispatcher.BOOK_SNIPPET_READY,
      () => this.loadPageList(),
      "abook-pagebreak-loaded"
    );
    Dispatcher.bindOnce(Dispatcher.BOOK_SNIPPET_READY, () => {
      if (
        !this.state.displaySearch &&
        (this.props.savedState["book-search"] &&
          this.props.savedState["book-search"]["query"])
      ) {
        this.setState({ displaySearch: true });
      }
      if (
        !this.state.displayHighlights &&
        (this.props.savedState["book-highlights"] &&
          this.props.savedState["book-highlights"]["filterCreator"])
      ) {
        this.setState({ displayHighlights: true });
      }
    });
    Dispatcher.bind(
      Dispatcher.BOOK_CHANGE,
      () => {
        this.setState(
          {
            pageNumber: this.props.book.currentPage,
            defaultPageNumber: this.props.book.currentPage
          },
          this._getCfiFromPageNumber
        );
      },
      "abook-change"
    );

    this.loadPageList();

    if (this.props.reading && !this.props.reading.started_at) {
      Feeny.UserReadings.start(
        this.props.assignment.uuid,
        this.props.reading.uuid
      );
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.reading !== this.props.reading) {
      this.setState({ displaySearch: false });
    }
  }

  loadPageList() {
    const { book, reading, startCFI, endCFI, exclusive } = this.props;
    if (startCFI && endCFI) {
      // TODO (wronfim) Use reading range_type instead
      const isCFI = startCFI[0] === "/" && endCFI[0] === "/";
      let pagelist;
      let pageBreaks = this.props.book.getPageBreaks();
      if (isCFI) {
        pagelist = BookUtils.getPageRangeFromCFIs(
          book.isbn,
          startCFI,
          endCFI,
          exclusive
        );
      } else if (reading.range_type === "page_index") {
        pagelist = BookUtils.getPageRangeFromIndex(
          book.isbn,
          parseInt(startCFI, 10),
          parseInt(endCFI, 10) + 1
        );
      } else {
        pagelist = BookUtils.getPageRangeFromLabel(
          book.isbn,
          startCFI,
          endCFI,
          exclusive
        );
      }

      this.setState(
        {
          pagelist,
          pageBreaks,
          pageNumber: this.props.book.currentPage,
          defaultPageNumber: this.props.book.currentPage
        },
        this._getCfiFromPageNumber
      );
    }
  }

  _goToCFI(event) {
    // go to cfi
    this.props.book.viewer.Book.goToCFI(event.target.value);
  }
  _goToPageChange(input) {
    this.setState({ pageNumber: input.value });
  }

  _goToPageClick(event) {
    $(event.currentTarget).select();
  }

  _goToPageBlur() {
    if (this.state.defaultPageNumber) {
      this.setState({ pageNumber: this.state.defaultPageNumber });
    }
  }

  _goToPageKeyUp(event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.keyCode === 13) {
      const page = $(event.currentTarget).val();
      this.props.book.goTo({ page });
    }
  }

  // NOTE (wronfim) Consider doing a refactoring on this function
  // Make it more generic, instead of depending on this.state
  _getCfiFromPageNumber() {
    const { pagelist, pageNumber } = this.state;
    const {
      book: { isbn }
    } = this.props;
    let value, start, end;
    if (pagelist && pagelist.length) {
      value = BookUtils.getCFIForPageLabel(isbn, pageNumber);
      if (value) {
        let startLabel = pagelist[0].label;
        start = BookUtils.getCFIForPageLabel(isbn, startLabel);

        let endLabel = pagelist[pagelist.length - 1].label;
        end = BookUtils.getCFIForPageLabel(isbn, endLabel);
      }
    } else if (this.props.reading && this.props.reading.range_type === "cfi") {
      value = this.props.book.pageCFI;
      start = this.props.startCFI;
      end = this.props.endCFI;
    }
    if (value && start && end) {
      if (Jigsaw.CFIUtils.compareCFIs(value, start) === -1) {
        value = start;
      } else if (Jigsaw.CFIUtils.compareCFIs(value, end) === 1) {
        value = end;
      }
      this.setState({ cfi: value });
      return value;
    }
  }

  _toggleSearch() {
    this.setState(prevState => {
      if (this.state.cfi) {
        const displaySearch = !prevState.displaySearch;
        displaySearch || this.props.book.viewer.Book.unhighlightTerms();
        return { displaySearch };
      }
    });
  }

  renderRightButtons() {
    if (this.props.hasRightButtons) {
      return (
        <div
          className="right-buttons"
          role="complementary"
          id="scrubber-container"
        >
          <button
            className={`noButton scrubber-button ${
              this.state.displaySearch ? "active" : ""
            }`}
            disabled={!this.state.cfi}
            onClick={this._toggleSearch}
          >
            <Isvg src="/img/search.svg" />
            <span className="offScreen">{i18n.t("base.search")}</span>
          </button>
          <button
            className={`noButton scrubber-button ${
              this.state.displayHighlights ? "active" : ""
            }`}
            onClick={() =>
              this.setState({
                displayHighlights: !this.state.displayHighlights
              })
            }
          >
            <Isvg src="/img/notebook_hover.svg" />
            <span className="offScreen">
              {i18n.t("reader.panels.notebook")}
            </span>
          </button>

          <ZoomButton
            id="aa-button"
            className={`${this.state.displayZoom ? "active" : ""}`}
            tooltip={i18n.t("reader.buttons.resize-text")}
            onAction={() =>
              this.setState({ displayZoom: !this.state.displayZoom })
            }
            onClose={() => this.setState({ displayZoom: false })}
            onMouseEnter={() => {}}
            onMouseLeave={() => {}}
            aria-expanded="false"
            isbn={this.props.book.isbn}
          />
        </div>
      );
    }
  }

  renderInputLabel() {
    return (
      <label htmlFor="go-to-page" className="go-to-page-label">
        {i18n.t("reader.go-to-page")}
      </label>
    );
  }

  renderGoToPage() {
    if (this.props.reading && this.state.pagelist) {
      return (
        <InlineSelect
          id="go-to-page"
          label={i18n.t("reader.go-to-page")}
          onChange={this._goToCFI.bind(this)}
          value={this.state.cfi}
        >
          {this.state.pagelist.map((page, index) => (
            <option key={index} value={page.cfi}>
              {page.label}
            </option>
          ))}
        </InlineSelect>
      );
    } else if (this.props.book.hasPageBreaks) {
      return (
        <GoToPageInputContainer>
          {this.renderInputLabel()}
          <input
            type="text"
            id="go-to-page"
            autoComplete="off"
            value={this.state.pageNumber}
            onChange={this._goToPageChange.bind(this)}
            onClick={this._goToPageClick.bind(this)}
            onKeyUp={this._goToPageKeyUp.bind(this)}
            onBlur={this._goToPageBlur.bind(this)}
          />
        </GoToPageInputContainer>
      );
    }
  }

  render() {
    const { displayHighlights } = this.state;
    return (
      <div
        id="book-reader"
        className={`
            ${this.props.className}
            ${displayHighlights === true ? "book-shrinked" : ""}`}
      >
        {this.state.displaySearch && (
          <BookSearch
            book={this.props.book}
            cfi={this.state.cfi}
            highlightsOn={displayHighlights}
            minimize={this.props.minimize}
            savedState={this.props.savedState["book-search"]}
          />
        )}
        <BookHighlights
          isbn={this.props.book.isbn}
          active={displayHighlights}
          minimize={this.props.minimize}
          startCFI={this.props.startCFI}
          endCFI={this.props.endCFI}
          rangeType={this.props.reading && this.props.reading.range_type}
          savedState={this.props.savedState["book-highlights"]}
        />
        {this.props.children}
        <div className="book-footer">
          {this.renderGoToPage()}
          <div className="book-title">{this.props.book.title}</div>
          {this.renderRightButtons()}
        </div>
      </div>
    );
  }
}

BookReader.propTypes = {
  book: PropTypes.object.isRequired,
  children: PropTypes.array.isRequired,
  hasRightButtons: PropTypes.bool,
  reading: PropTypes.object,
  startCFI: PropTypes.string,
  endCFI: PropTypes.string,
  assignment: PropTypes.object
};

BookReader.defaultProps = {
  savedState: {}
};
